import { ReactElement, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { addBreadcrumb } from "@sentry/react";
import getLocationFlags from "utilities/getLocationFlags";
import { queryKeys } from "utilities/constants";
import updateLocationFlags from "utilities/updateLocationFlags";
import useApi from "hooks/useApi";
import { useLocation } from "./LocationContext";
import { useNotifications } from "./NotificationsContext";

export interface LocationFlag {
    name: string;
    enabled: boolean;
}

export interface LocationFlagsContextProps {
    checkIfFlagIsEnabled: (flagName: string) => boolean;
    updateLocationFlags: (updatedLocationFlags: LocationFlag[]) => Promise<void>;
    isLoadingLocationFlags: boolean;
    locationFlags: LocationFlag[] | undefined;
}

export const LocationFlagsContext = createContext<LocationFlagsContextProps | null>(null);

LocationFlagsContext.displayName = "LocationFlagsContext";

export function LocationFlagsProvider(props: any): ReactElement {
    const [initialLocationFlags, setInitialLocationFlags] = useState<LocationFlag[] | undefined>(undefined);
    const [locationFlags, setLocationFlags] = useState<LocationFlag[] | undefined>(undefined);

    const { currentLocation, partnerId } = useLocation();
    const { locationId, posType } = currentLocation ?? {};
    const apiClient = useApi();

    const { notify } = useNotifications();

    const queryClient = useQueryClient();

    const { isLoading: isLoadingLocationFlags, refetch: refetchLocationFlags } = useQuery(
      queryKeys.locationFlags,
        async () => 
            await getLocationFlags({
              apiClient,
              locationId,
              partnerId
            }),
        {
          enabled: !!locationId && !!partnerId,
          onSuccess(data) {
              setInitialLocationFlags(data.resource);
          },
          onError: (error: any) => {
            notify({
              type: "error",
              message: `Failed to fetch location flags: ${error?.message || error}`,
            });
          },
        }
    );

    const updateLocationFlagsMutation = useMutation(
        async (updatedLocationFlags: LocationFlag[]) => {
            if (!locationId || !partnerId) {
                throw new Error("updateLocationFlagsMutation: Location ID and Partner ID are required to update Location Flags data.");
            }

            if (!locationFlags) {
                throw new Error("updateLocationFlagsMutation: locationFlags is undefined.");
            }

            addBreadcrumb({
              level: "info",
              category: "user_action",
              message: `Update locationFlags for locationId:${currentLocation?.id}`,
            });

            const response = await updateLocationFlags({
              apiClient,
              locationFlags: updatedLocationFlags,
              locationId,
              partnerId,
              posType,
            });

            return response;
        }, {
          onSuccess: async () => {
            notify({
              type: "success",
              message: "Location Flags updated successfully.",
            });

            setInitialLocationFlags(undefined);
            setLocationFlags(undefined);

            // Refetch location flags to get the latest data
            void refetchLocationFlags();
          },
        }
    );

    const handleUpdateLocationFlags = useCallback(
      async (updatedLocationFlags: LocationFlag[]) => {
        if (updatedLocationFlags !== initialLocationFlags) {
            updateLocationFlagsMutation.mutate(updatedLocationFlags);
        }
      },
      [initialLocationFlags, updateLocationFlagsMutation],
    );

    const checkIfFlagIsEnabled = useCallback(
      (flagName: string) => {
        return locationFlags?.find(flag => flag.name === flagName && flag.enabled)?.enabled ?? false;
      },
      [locationFlags],
    );
    
    
    useEffect(() => {
        if (initialLocationFlags && !locationFlags) {
            setLocationFlags(initialLocationFlags);
        }
    }, [initialLocationFlags, locationFlags]);
    

    // reset queries on location change
    useEffect(() => {
        if (currentLocation?.id) {
            queryClient.setQueryData(queryKeys.locationFlags, undefined);
            setInitialLocationFlags(undefined);
            setLocationFlags(undefined);
            void refetchLocationFlags();
        }
    }, [currentLocation?.id, queryClient, refetchLocationFlags]);

    const value: LocationFlagsContextProps = {
      checkIfFlagIsEnabled,
      isLoadingLocationFlags,
      locationFlags,
      updateLocationFlags: handleUpdateLocationFlags,
    };

    return (<LocationFlagsContext.Provider value={value} {...props} />) as ReactElement;
}

export function useLocationFlags(): LocationFlagsContextProps {
    const context = useContext(LocationFlagsContext);

    if (context === undefined) {
        throw new Error("useLocationFlags must be used within a LocationFlagsProvider");
    }

    // Because we are getting the context from LocationFlagsProvider here, if context is
    // not 'undefined' (ie: the LocationFlagsProvider is not a parent of this component),
    // we can be sure context is not 'null' here
    if (context === null) {
        throw new Error("LocationFlagsProvider supplied null context");
    }

    return context;
}